import DynamicComponent from "@/components/DynamicComponentLoader";
import { DynamicFooter, DynamicHeader } from "@/components/DynamicHeaderFooter";
import { MetaHead } from "@/components/MetaHead";
import { useConfig } from "@/context/ConfigContext";
import { getInitialSeoForPage } from "@/lib/serverSeoConfig";
import { useBanners } from "@/hooks/useBanners";
import { useBrands } from "@/hooks/useBrands";
import { useCollections } from "@/hooks/useCollections";
import { useFeatures } from "@/hooks/useFeatures";
import { useProductCategories } from "@/hooks/useProductCategories";
import { useProducts } from "@/hooks/useProducts";
import { getSortedComponents } from "@/utils/componentMapper";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useMemo, useState, useEffect } from "react";
import {
  createBuilderBridge,
  getBuilderModeFromLocation,
  handleBuilderComponentClick,
} from "@/lib/builderBridge";

// Dynamic imports untuk wrapper components (fallback only)
const DynamicBanner = dynamic(
  () =>
    import("@/components/DynamicComponentRenderer").then(
      (mod) => mod.DynamicBanner,
    ),
  { ssr: false },
);
const DynamicCategory = dynamic(
  () =>
    import("@/components/DynamicComponentRenderer").then(
      (mod) => mod.DynamicCategory,
    ),
  { ssr: false },
);
const DynamicProductList = dynamic(
  () =>
    import("@/components/DynamicComponentRenderer").then(
      (mod) => mod.DynamicProductList,
    ),
  { ssr: false },
);
const DynamicCTA = dynamic(
  () =>
    import("@/components/DynamicComponentRenderer").then(
      (mod) => mod.DynamicCTA,
    ),
  { ssr: false },
);
const DynamicCollection = dynamic(
  () =>
    import("@/components/DynamicComponentRenderer").then(
      (mod) => mod.DynamicCollection,
    ),
  { ssr: false },
);
const DynamicBlog = dynamic(
  () =>
    import("@/components/DynamicComponentRenderer").then(
      (mod) => mod.DynamicBlog,
    ),
  { ssr: false },
);
const DynamicTestimonial = dynamic(
  () =>
    import("@/components/DynamicComponentRenderer").then(
      (mod) => mod.DynamicTestimonial,
    ),
  { ssr: false },
);

function ProductCategorySection({
  componentKey,
  componentConfig,
  categories,
  categoriesLoading,
  categoriesError,
  imageBaseUrl,
  selectedTabIndex,
  setSelectedTabIndex,
  isBuilderMode,
}) {
  const itemsLimit = useMemo(() => {
    const rawLimit = componentConfig?.customization?.items_limit;
    const parsed = Number.parseInt(rawLimit, 10);
    return Number.isNaN(parsed) || parsed <= 0 ? 8 : parsed;
  }, [componentConfig?.customization?.items_limit]);

  const activeCategory = useMemo(() => {
    if (!Array.isArray(categories) || selectedTabIndex <= 0) return null;
    return categories[selectedTabIndex - 1] || null;
  }, [categories, selectedTabIndex]);

  /** Proxy: /api/products?...&category=ipad&page=1&per_page=12 (slug prioritas vs id) */
  const productListParams = useMemo(() => {
    const base = { page: 1, per_page: itemsLimit };
    if (selectedTabIndex <= 0 || !activeCategory) return base;
    const slug =
      activeCategory.slug ??
      activeCategory.slug_url ??
      activeCategory.slug_path ??
      activeCategory.handle;
    if (slug != null && String(slug).trim() !== "") {
      return { ...base, category: String(slug).trim() };
    }
    if (activeCategory.id != null) {
      return { ...base, category_id: activeCategory.id };
    }
    return base;
  }, [activeCategory, itemsLimit, selectedTabIndex]);

  const {
    products: componentProducts,
    loading: componentProductsLoading,
    error: componentProductsError,
  } = useProducts({
    params: productListParams,
  });

  if (
    !isBuilderMode &&
    !componentProductsLoading &&
    !componentProductsError &&
    componentProducts.length === 0
  ) {
    return null;
  }

  return (
    <DynamicComponent
      key={componentKey}
      componentConfig={componentConfig}
      products={componentProducts}
      categories={categories}
      loading={componentProductsLoading || categoriesLoading}
      error={componentProductsError || categoriesError}
      imageBaseUrl={imageBaseUrl}
      selectedTabIndex={selectedTabIndex}
      setSelectedTabIndex={setSelectedTabIndex}
    />
  );
}

export default function Home({ initialSeo }) {
  const router = useRouter();
  const { config, configData } = useConfig();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (router.query?.r !== "true") return;

    // Hapus semua localStorage
    localStorage.clear();

    // Hapus semua cache (service worker / Cache API)
    if ("caches" in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    // Hapus query ?r=true dari URL tanpa reload
    router.replace("/", undefined, { shallow: true });
  }, [router.query?.r]);
  const imageBaseUrl = config?.api?.imageUrl || "";
  const uiComponents = configData?.ui_components || config?.ui_components;
  const isBuilderMode = getBuilderModeFromLocation();

  // Get sorted components dari ui_components
  const sortedComponents = useMemo(() => {
    if (!uiComponents) return [];
    return getSortedComponents(uiComponents);
  }, [uiComponents]);

  // Fetch data untuk semua component types
  const {
    banners,
    loading: bannersLoading,
    error: bannersError,
  } = useBanners({
    params: { status: "Active" },
  });

  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
  } = useProductCategories();

  const {
    collections,
    loading: collectionsLoading,
    error: collectionsError,
  } = useCollections();

  const {
    products: allProducts,
    loading: productsLoading,
    error: productsError,
  } = useProducts({
    params: {
      per_page: 8,
    },
  });

  const { brands, loading: brandsLoading, error: brandsError } = useBrands();

  const {
    features,
    loading: featuresLoading,
    error: featuresError,
  } = useFeatures();

  // State untuk track selectedTabIndex untuk product-category components
  // Map component ID ke tab index untuk support multiple product-category components
  const [selectedTabIndices, setSelectedTabIndices] = useState({});

  useEffect(() => {
    const bridge = createBuilderBridge();
    bridge?.applyMarkers?.();
    return () => bridge?.dispose?.();
  }, []);

  // Helper untuk get/set selectedTabIndex per component
  const getSelectedTabIndex = (componentId) => {
    return selectedTabIndices[componentId] || 0;
  };

  const setSelectedTabIndex = (componentId, index) => {
    setSelectedTabIndices((prev) => ({
      ...prev,
      [componentId]: index,
    }));
  };

  // Jika ada ui_components, gunakan DynamicComponent langsung
  if (sortedComponents && sortedComponents.length > 0) {
    return (
      <>
        <MetaHead
          pagePath="/"
          title={initialSeo?.title}
          description={initialSeo?.description}
          ogImage={initialSeo?.ogImage}
          ogUrl={initialSeo?.ogUrl}
        />
        <DynamicHeader />
        <div className="overflow-x-hidden">
          {sortedComponents.map((componentConfig) => {
            // Skip header, footer, dan landing-content
            // landing-content hanya dirender di halaman promo
            if (
              componentConfig.component_type === "header" ||
              componentConfig.component_type === "footer" ||
              componentConfig.component_type === "landing-content"
            ) {
              return null;
            }

            const componentId = Number(componentConfig.id);
            if (!Number.isFinite(componentId)) {
              console.warn(
                "[BuilderMode] Komponen tanpa ID stabil dilewati dari marker builder:",
                componentConfig?.component_type,
              );
              return null;
            }
            const componentKey = `${componentConfig.component_type}-${componentId}`;
            const shouldHideEmptySection = (items, loading, error) =>
              !isBuilderMode &&
              !loading &&
              !error &&
              (!Array.isArray(items) || items.length === 0);
            const wrapBuilderComponent = (node) => {
              if (!isBuilderMode) return node;
              const builderLabel = `${componentConfig.component_type} #${componentId}`;
              return (
                <div
                  key={`builder-wrap-${componentKey}`}
                  data-builder-id={componentId}
                  data-builder-type={componentConfig.component_type}
                  data-builder-label={builderLabel}
                  onClickCapture={(event) => handleBuilderComponentClick(event, componentId)}
                >
                  {node}
                </div>
              );
            };

            // Render DynamicComponent dengan props sesuai component_type
            switch (componentConfig.component_type) {
              case "banner":
                if (
                  shouldHideEmptySection(
                    banners,
                    bannersLoading,
                    bannersError,
                  )
                ) {
                  return null;
                }
                return wrapBuilderComponent(
                  <DynamicComponent
                    key={componentKey}
                    componentConfig={componentConfig}
                    banners={banners}
                    loading={bannersLoading}
                    error={bannersError}
                    imageBaseUrl={imageBaseUrl}
                  />
                );
              case "category":
                if (
                  shouldHideEmptySection(
                    categories,
                    categoriesLoading,
                    categoriesError,
                  )
                ) {
                  return null;
                }
                return wrapBuilderComponent(
                  <DynamicComponent
                    key={componentKey}
                    componentConfig={componentConfig}
                    categories={categories}
                    loading={categoriesLoading}
                    error={categoriesError}
                  />
                );
              case "product-category": {
                const selectedTabIndex = getSelectedTabIndex(componentId);

                return wrapBuilderComponent(
                  <ProductCategorySection
                    key={componentKey}
                    componentKey={componentKey}
                    componentConfig={componentConfig}
                    categories={categories}
                    categoriesLoading={categoriesLoading}
                    categoriesError={categoriesError}
                    imageBaseUrl={imageBaseUrl}
                    selectedTabIndex={selectedTabIndex}
                    setSelectedTabIndex={(index) => setSelectedTabIndex(componentId, index)}
                    isBuilderMode={isBuilderMode}
                  />
                );
              }
              case "product-list":
                if (
                  shouldHideEmptySection(
                    allProducts,
                    productsLoading,
                    productsError,
                  )
                ) {
                  return null;
                }
                return wrapBuilderComponent(
                  <DynamicComponent
                    key={componentKey}
                    componentConfig={componentConfig}
                    products={allProducts}
                    loading={productsLoading}
                    error={productsError}
                    imageBaseUrl={imageBaseUrl}
                  />
                );
              case "brand":
                if (
                  shouldHideEmptySection(brands, brandsLoading, brandsError)
                ) {
                  return null;
                }
                return wrapBuilderComponent(
                  <DynamicComponent
                    key={componentKey}
                    componentConfig={componentConfig}
                    brands={brands}
                    loading={brandsLoading}
                    error={brandsError}
                    imageBaseUrl={imageBaseUrl}
                  />
                );
              case "blog":
                return wrapBuilderComponent(
                  <DynamicBlog
                    key={componentKey}
                    componentConfig={componentConfig}
                    imageBaseUrl={imageBaseUrl}
                  />
                );
              case "collection":
              case "product-showcase":
                if (
                  shouldHideEmptySection(
                    collections,
                    collectionsLoading,
                    collectionsError,
                  )
                ) {
                  return null;
                }
                return wrapBuilderComponent(
                  <DynamicComponent
                    key={componentKey}
                    componentConfig={componentConfig}
                    collections={collections}
                    loading={collectionsLoading}
                    error={collectionsError}
                    imageBaseUrl={imageBaseUrl}
                  />
                );
              case "cta":
                return wrapBuilderComponent(
                  <DynamicComponent
                    key={componentKey}
                    componentConfig={componentConfig}
                  />
                );
              case "testimonial":
                return wrapBuilderComponent(
                  <DynamicTestimonial
                    key={componentKey}
                    componentConfig={componentConfig}
                    imageBaseUrl={imageBaseUrl}
                  />
                );
              case "feature":
                if (
                  shouldHideEmptySection(
                    features,
                    featuresLoading,
                    featuresError,
                  )
                ) {
                  return null;
                }
                return wrapBuilderComponent(
                  <DynamicComponent
                    key={componentKey}
                    componentConfig={componentConfig}
                    features={features}
                    loading={featuresLoading}
                    error={featuresError}
                    imageBaseUrl={imageBaseUrl}
                  />
                );
              default:
                // Untuk component types lain, gunakan DynamicComponent dengan imageBaseUrl
                return wrapBuilderComponent(
                  <DynamicComponent
                    key={componentKey}
                    componentConfig={componentConfig}
                    imageBaseUrl={imageBaseUrl}
                  />
                );
            }
          })}
        </div>
        <DynamicFooter />
        {isBuilderMode ? (
          <style jsx global>{`
            [data-builder-id] {
              position: relative;
              cursor: ${router.query.componentPicker === "1" ? "crosshair" : "default"};
            }
            [data-builder-id].builder-hovered {
              outline: 2px dashed rgba(37, 99, 235, 0.75);
              outline-offset: 2px;
            }
            [data-builder-id].builder-selected {
              z-index: 1;
              outline: none;
              box-shadow:
                inset 0 3px 0 #10b981,
                inset 0 -3px 0 #10b981,
                inset 3px 0 0 #10b981,
                inset -3px 0 0 #10b981;
            }
            [data-builder-id].builder-selected::after,
            [data-builder-id].builder-hovered::after {
              content: attr(data-builder-label);
              position: absolute;
              top: 6px;
              left: 6px;
              z-index: 60;
              font-size: 11px;
              line-height: 1;
              font-weight: 700;
              letter-spacing: 0.01em;
              padding: 5px 7px;
              border-radius: 6px;
              color: #ffffff;
              background: rgba(2, 132, 199, 0.95);
              pointer-events: none;
              text-transform: capitalize;
            }
            [data-builder-id].builder-hovered::after {
              background: rgba(37, 99, 235, 0.92);
            }
            [data-builder-id].builder-selected::after {
              background: rgba(5, 150, 105, 0.96);
            }
          `}</style>
        ) : null}
      </>
    );
  }

  // Fallback ke wrapper components jika tidak ada ui_components
  return (
    <>
      <MetaHead
        pagePath="/"
        title={initialSeo?.title}
        description={initialSeo?.description}
        ogImage={initialSeo?.ogImage}
        ogUrl={initialSeo?.ogUrl}
        googleSiteVerification={initialSeo?.googleSiteVerification}
        bingSiteVerification={initialSeo?.bingSiteVerification}
        yandexSiteVerification={initialSeo?.yandexSiteVerification}
      />
      <DynamicHeader />
      <div className="overflow-x-hidden pt-0 pb-20 md:pt-6">
        {!bannersLoading && banners.length === 0 ? null : (
          <DynamicBanner imageBaseUrl={imageBaseUrl} />
        )}
        <div className="px-2 sm:px-4 py-8 sm:py-12 md:py-12 md:px-12 lg:px-12 space-y-16 sm:space-y-24">
          {!categoriesLoading && categories.length === 0 ? null : (
            <DynamicCategory
              categories={categories}
              loading={categoriesLoading}
              error={categoriesError}
            />
          )}
          {!collectionsLoading && collections.length === 0 ? null : (
            <DynamicCollection
              collections={collections}
              loading={collectionsLoading}
              error={collectionsError}
              imageBaseUrl={imageBaseUrl}
            />
          )}
          {!productsLoading && allProducts.length === 0 ? null : (
            <DynamicProductList
              collections={collections}
              imageBaseUrl={imageBaseUrl}
            />
          )}
          <DynamicCTA />
          <DynamicBlog />
        </div>
      </div>
      <DynamicFooter />
    </>
  );
}

export async function getServerSideProps(context) {
  if (context?.res) {
    context.res.setHeader(
      "Cache-Control",
      "public, s-maxage=300, stale-while-revalidate=600",
    );
  }
  const initialSeo = await getInitialSeoForPage(context.req, "/");
  return { props: { initialSeo: initialSeo || null } };
}
